import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../../../../components/ui/atoms/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../components/ui/organisms/dialog';
import { ClientNameDialog } from './ClientNameDialog';

// Each document implies its own file format, so there is no separate format
// picker — the extension is shown on the option itself.
export type ExportDocument = 'timesheet' | 'entries' | 'csv' | 'SAP';

const DOCUMENTS: { value: ExportDocument; label: string; extension: string; description: string }[] = [
  {
    value: 'timesheet',
    label: 'Customer Timesheet',
    extension: '.xlsx',
    description: 'The formatted Finaira timesheet sent to customers. One sheet per employee, with logo and signature blocks.',
  },
  {
    value: 'SAP',
    label: 'SAP Ready (Beta)',
    extension: '.xlsx',
    description: 'The SAP-ready export, with one sheet per employee and the columns required for SAP import. (Beta: Not yet ready.)',
  },
  {
    value: 'entries',
    label: 'Entry List',
    extension: '.xlsx',
    description: 'A flat table of every matching entry (date, employee, project, phase, hours, status, notes) as a styled spreadsheet.',
  },
  {
    value: 'csv',
    label: 'Plain CSV',
    extension: '.csv',
    description: 'The same flat table as plain text, for importing into other tools or scripts.',
  },
];

function OptionCard({
  selected, title, extension, description, onClick,
}: {
  selected: boolean;
  title: string;
  extension: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-md border p-3 text-left transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 ${
            selected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
          }`}
        />
        <span className="text-sm font-medium">{title}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {extension}
        </span>
      </div>
      <p className="mt-1 pl-5 text-xs text-muted-foreground">{description}</p>
    </button>
  );
}

export interface ExportOptions {
  /** Written into the Customer Timesheet's "Client Name" row. */
  clientName?: string;
}

export function ExportDialog({
  open, onOpenChange, onExport, isExporting, entryCount, message, error, defaultClientName = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (document: ExportDocument, options?: ExportOptions) => void;
  isExporting: boolean;
  /** Prefill for the timesheet's client-name prompt. */
  defaultClientName?: string;
  /** Entries currently matching the filters, shown so the admin knows the scope. */
  entryCount: number;
  /** Result of the last run. Shown here rather than on the page behind, which
      the overlay hides — the dialog stays open so it can actually be read. */
  message?: string;
  error?: string;
}) {
  const [selected, setSelected] = useState<ExportDocument>('timesheet');
  const [clientPromptOpen, setClientPromptOpen] = useState(false);

  // The timesheet needs a client name first; every other document exports directly.
  const handleExportClick = () => {
    if (selected === 'timesheet') {
      setClientPromptOpen(true);
      return;
    }
    onExport(selected);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Export entries</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Exports what you see: every entry matching your current filters
            {entryCount > 0 ? ` (${entryCount} on this page).` : '.'} The Customer Timesheet and SAP exports include approved entries only.
          </p>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Document</p>
            {DOCUMENTS.map((d) => (
              <OptionCard
                key={d.value}
                selected={selected === d.value}
                title={d.label}
                extension={d.extension}
                description={d.description}
                onClick={() => setSelected(d.value)}
              />
            ))}
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
          )}
          {message && !error && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-foreground">{message}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isExporting}>
              {message || error ? 'Close' : 'Cancel'}
            </Button>
            <Button
              size="sm"
              onClick={handleExportClick}
              disabled={isExporting}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <ClientNameDialog
      open={clientPromptOpen}
      onOpenChange={setClientPromptOpen}
      defaultValue={defaultClientName}
      isExporting={isExporting}
      onConfirm={(clientName) => {
        setClientPromptOpen(false);
        onExport('timesheet', { clientName });
      }}
    />
    </>
  );
}
